import { useCallback, useEffect, useState } from "react";
import { Store, Image as ImageIcon, Type, Contact, Sparkles, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { makeElement } from "@/lib/card";
import BrandDialog from "@/components/BrandDialog";

const r2 = (n) => Math.round(n * 100) / 100;

const contactText = (b) => [
  [b.phone, b.email].filter(Boolean).join("  ·  "),
  b.website?.replace(/^https?:\/\//, ""),
  b.address,
].filter(Boolean).join("\n");

function logoEl(tpl, b) {
  const h = r2(Math.min(10, tpl.height * 0.2));
  return makeElement("image", tpl, { src: b.logo, fit: "contain", x: 4, y: 4, w: r2(h * 1.6), h });
}

function nameEl(tpl, b, x = 4, y = 4) {
  return { ...makeElement("heading", tpl), text: b.store_name, x, y, w: r2(tpl.width - x - 4), h: 5, fontSize: 9 };
}

function contactEl(tpl, b) {
  const lines = contactText(b).split("\n").length;
  const h = r2(lines * 2.4 + 0.6);
  return { ...makeElement("text", tpl), text: contactText(b), x: 4, y: r2(tpl.height - h - 4), w: r2(tpl.width - 8), h, fontSize: 5, color: "#334155", lineHeight: 1.25 };
}

export function brandKit(tpl, b) {
  const els = [];
  let nx = 4;
  if (b.logo) { const l = logoEl(tpl, b); els.push(l); nx = r2(l.x + l.w + 2.5); }
  if (b.store_name) {
    els.push(nameEl(tpl, b, nx, 4.5));
    if (b.tagline) els.push({ ...makeElement("text", tpl), text: b.tagline, x: nx, y: 9.5, w: r2(tpl.width - nx - 4), h: 3, fontSize: 4.5, color: "#64748B" });
  }
  if (contactText(b)) els.push(contactEl(tpl, b));
  return els;
}

const BrandBtn = ({ icon: Icon, label, onClick, disabled, testId }) => (
  <button onClick={onClick} disabled={disabled} data-testid={testId}
    className="flex items-center gap-2 px-2.5 h-9 rounded-md border border-slate-800 bg-slate-950/60 text-[11px] text-slate-300 hover:border-amber-500/60 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
    <Icon size={14} className="text-amber-400 shrink-0" /> {label}
  </button>
);

export default function BrandTools({ tpl, addEls }) {
  const [b, setB] = useState(null);
  const load = useCallback(() => api.get("/brand").then((r) => setB(r.data)), []);
  useEffect(() => { load(); }, [load]);

  if (!b) return <Loader2 size={14} className="animate-spin text-slate-500" />;
  const empty = !b.logo && !b.store_name && !contactText(b);

  return (
    <div className="space-y-2" data-testid="brand-tools">
      {empty ? (
        <p className="text-[11px] text-slate-500 leading-relaxed" data-testid="brand-tools-empty">Save your store logo and contacts once, then drop them onto any card.</p>
      ) : (
        <>
          <button onClick={() => addEls(brandKit(tpl, b))} data-testid="brand-add-kit"
            className="w-full flex items-center justify-center gap-2 h-10 rounded-lg bg-gradient-to-r from-amber-500/20 to-amber-300/10 border border-amber-500/40 text-amber-100 text-xs font-semibold hover:border-amber-400 transition-colors">
            <Sparkles size={14} /> Add full brand kit
          </button>
          <div className="grid grid-cols-2 gap-1.5">
            <BrandBtn icon={ImageIcon} label="Logo" disabled={!b.logo} onClick={() => addEls([logoEl(tpl, b)])} testId="brand-add-logo" />
            <BrandBtn icon={Type} label="Store name" disabled={!b.store_name} onClick={() => addEls([nameEl(tpl, b)])} testId="brand-add-name" />
            <BrandBtn icon={Contact} label="Contacts" disabled={!contactText(b)} onClick={() => addEls([contactEl(tpl, b)])} testId="brand-add-contacts" />
            <BrandBtn icon={Store} label="Tagline" disabled={!b.tagline}
              onClick={() => addEls([{ ...makeElement("text", tpl), text: b.tagline, fontSize: 5, color: "#64748B" }])} testId="brand-add-tagline" />
          </div>
        </>
      )}
      <BrandDialog onSaved={load} label={empty ? "Set up store profile" : "Edit store profile"} />
    </div>
  );
}
